import { jsPDF } from "jspdf";
import fempulseLogo from "../assets/images/fempulseLogo.png";

const BLACK = [51, 51, 51];
const GRAY_HEADER = [74, 144, 217];
const HEADER_TEXT = [255, 255, 255];
const GRAY_TEXT = [100, 100, 100];
const GREEN = [77, 182, 172];
const RED = [211, 47, 47];
const AMBER = [237, 194, 75];
const BLUE = [0, 169, 224];
const BORDER = [200, 220, 230];
const HEAD_H = 14;
const DATA_H = 12;
const LINE_H = 4;
const FS = 9;
const FS_HEAD = 9;

const lighten = (rgb, factor = 0.85) => rgb.map(c => Math.round(c + (255 - c) * factor));

const fmtDateLong = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const fmtDateTime = (str) => {
  if (!str) return "–";
  const d = new Date(str);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtDateShort = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
  const currentYear = new Date().getFullYear();
  if (d.getFullYear() !== currentYear) {
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

function wrapText(doc, text, maxW) {
  if (!text) return ["—"];
  const words = String(text).split(" ");
  const lines = [];
  let cur = "";
  for (const word of words) {
    let w = word;
    while (doc.getTextWidth(w) > maxW) {
      let chunk = "";
      for (const ch of w) {
        if (doc.getTextWidth(chunk + ch) > maxW) break;
        chunk += ch;
      }
      if (!chunk) chunk = w[0];
      const test = cur ? cur + " " + chunk : chunk;
      if (cur && doc.getTextWidth(test) > maxW) {
        lines.push(cur);
        cur = chunk;
      } else cur = test;
      w = w.slice(chunk.length);
    }
    if (w) {
      const test = cur ? cur + " " + w : w;
      if (cur && doc.getTextWidth(test) > maxW) {
        lines.push(cur);
        cur = w;
      } else cur = test;
    }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : ["—"];
}

function drawRoundedRect(doc, x, y, w, h, r, style = "F") {
  doc.roundedRect(x, y, w, h, r, r, style);
}

function drawBadge(doc, x, y, text, bgColor, textColor, align = "left") {
  const paddingH = 3;
  const paddingV = 1.5;
  doc.setFontSize(8);
  const tw = doc.getTextWidth(text);
  const w = tw + paddingH * 2;
  const h = 5;
  if (align === "right") {
    x = x - w;
  }
  doc.setFillColor(...bgColor);
  drawRoundedRect(doc, x, y, w, h, 2, "F");
  doc.setTextColor(...textColor);
  doc.text(text, x + paddingH, y + 3.6);
  return w;
}

function drawProgressBar(doc, x, y, w, current, total) {
  const h = 2;
  const r = 1;
  const progress = Math.min(1, Math.max(0, current / total));
  doc.setFillColor(235, 235, 235);
  drawRoundedRect(doc, x, y, w, h, r, "F");
  doc.setFillColor(...BLUE);
  drawRoundedRect(doc, x, y, w * progress, h, r, "F");
}

function drawMetricBox(doc, x, y, w, label, value, sub = "") {
  doc.setFontSize(8);
  doc.setTextColor(...GRAY_TEXT);
  doc.text(label.toUpperCase(), x, y);
  doc.setFontSize(14);
  doc.setTextColor(...BLACK);
  const valStr = String(value);
  doc.text(valStr, x, y + 6);
  if (sub) {
    const valW = doc.getTextWidth(valStr);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...BLACK);
    doc.text(sub, x + valW + 2, y + 6);
    doc.setFont("helvetica", "normal");
  }
}

function drawCell(doc, x, y, w, h, lines, opts = {}) {
  const { bg, bold, color, fontSize = FS, align = "left" } = opts;
  if (bg) {
    doc.setFillColor(...bg);
    doc.rect(x, y, w, h, "F");
  }
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.rect(x, y, w, h);
  doc.setFont("helvetica", bold ? "bold" : "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(...(color || BLACK));

  lines.forEach((line, li) => {
    if (5 + li * LINE_H < h - 2) {
      let textX = x + 2;
      if (align === "center") {
        const tw = doc.getTextWidth(line);
        textX = x + (w - tw) / 2;
      }
      doc.text(String(line), textX, y + 7 + li * LINE_H);
    }
  });
}

const COLS = [
  { label: "Date", w: 22, align: "left" },
  { label: "Voids", w: 17, align: "left" },
  { label: "Leaks", w: 14, align: "left" },
  { label: "Fluids\n(oz)", w: 17, align: "left" },
  { label: "Urgency", w: 15, align: "left" },
  { label: "Intake", w: 17, align: "left" },
  { label: "Activity", w: 17, align: "left" },
  { label: "Status", w: 18, align: "left" },
  { label: "Reason", w: 55, align: "left" },
  { label: "Last Modified", w: 62, align: "left" },
];

export function generateUserAnalysisPDF({
  patientInfo = {},
  tableRows = [],
  averages = {},
  excludedDates = new Set(),
  startDate,
  endDate,
  globalAdded = new Set(),
  fileName = "",
  shouldSave = true,  // if false, returns the jsPDF doc instead of downloading
}) {
  const includedCount = tableRows.filter((row) => {
    const rec = row.record;
    return (!rec || rec.dayInclude !== "no") && !excludedDates.has(row.date);
  }).length;
  const excludedCount = tableRows.length - includedCount;

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4", compress: true });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const mg = 14;

  let curY = mg;

  const ensureSpace = (needed) => {
    if (curY + needed > pageH - mg) {
      doc.addPage();
      curY = mg;
    }
  };

  let complianceOffset = 0;
  let issues = [];
  const status = (patientInfo.compliance_status || "no-data").toLowerCase();

  if (patientInfo.includeCompliance) {
    complianceOffset = 7;
    if (status === "non-compliant") {
      try {
        const parsed = typeof patientInfo.compliance_issues === "string"
          ? JSON.parse(patientInfo.compliance_issues)
          : patientInfo.compliance_issues;
        if (Array.isArray(parsed) && parsed.length > 0) {
          issues = parsed;
          complianceOffset += 7 + issues.length * 5;
        }
      } catch (e) {}
    }
  }

  const headerH = 45 + complianceOffset;
  const logoW = 70; // Slightly increased width for PDF logo
  const logoH = 19; // Slightly increased height for PDF logo
  try {
    doc.addImage(fempulseLogo, "PNG", pageW - mg - logoW, curY + 2, logoW, logoH);
  } catch (e) { }


  const studyName = patientInfo.studyName || "EVANESCE II";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...BLUE);
  const studyTitleWithSite = patientInfo.siteName ? `${studyName} (${patientInfo.siteName})` : studyName;
  doc.text(studyTitleWithSite, mg, curY + 12);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...GRAY_TEXT);
  const reportStatus = patientInfo.status || "Active";
  const subtitleText = reportStatus === "Total Baseline Averages"
    ? "Total Baseline Averages Summary"
    : `(${reportStatus}) Average Summary`;
  doc.text(subtitleText, mg, curY + 20);
  const genDate = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  doc.text(`Generated On: ${genDate}.`, mg, curY + 28);

  let dateRangeText = "";
  if (startDate && endDate && !isNaN(new Date(startDate + "T00:00:00").getTime())) {
    const getDisplayDates = () => {
      let minD = startDate;
      let maxD = endDate;
      if (globalAdded && globalAdded.size > 0) {
        globalAdded.forEach(d => {
          if (d < minD) minD = d;
          if (d > maxD) maxD = d;
        });
      }
      return { start: minD, end: maxD };
    };
    const displayDates = getDisplayDates();
    const d1 = new Date(displayDates.start + "T00:00:00");
    const d2 = new Date(displayDates.end + "T00:00:00");
    const diffDays = Math.round((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
    dateRangeText = `${fmtDateLong(displayDates.start)} – ${fmtDateLong(displayDates.end)} (${diffDays} days)`;
  } else {
    dateRangeText = patientInfo.dateRange || "N/A";
  }
  doc.text(`Date Range: ${dateRangeText}`, mg, curY + 35);

  if (patientInfo.includeCompliance) {
    const complianceY = curY + 42;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor("#000000");

    // Compliance Status
    const statusLabel = "Compliance Status: ";
    doc.text(statusLabel, mg, complianceY);
    const statusVal = patientInfo.compliance_status || "no-data";
    const statusValX = mg + doc.getTextWidth(statusLabel);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(statusVal.toLowerCase() === "non-compliant" ? "red" : "green");
    doc.text(statusVal, statusValX, complianceY);

    // Compliance Issues (only if non-compliant)
    if (statusVal.toLowerCase() === "non-compliant" && issues.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor("#000000");
      doc.text("Compliance Issues:", mg, complianceY + 7);

      doc.setFont("helvetica", "normal");
      issues.forEach((issue, index) => {
        doc.text(`${index + 1}. ${issue}`, mg + 6, complianceY + 12 + (index * 5));
      });
    }
  }

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.setTextColor(...RED);
  doc.text(`* Summary data till ${yesterdayStr}`, mg, curY + 42 + complianceOffset);
  // Reset font back to normal after the note
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...BLACK);

  curY += headerH;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.5);
  doc.line(mg, curY, pageW - mg, curY);
  curY += 8;


  const cardH = 45;
  ensureSpace(cardH + 10);


  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.setFillColor(255, 255, 255);
  drawRoundedRect(doc, mg, curY, pageW - mg * 2, cardH, 2, "FD");


  const avatarSize = 10;
  const userPrefix = patientInfo.patientName || (patientInfo.patientId || "").split("@")[0] || "—";
  doc.setFillColor(...GRAY_HEADER);
  drawRoundedRect(doc, mg + 5, curY + 5, avatarSize, avatarSize, avatarSize / 2, "F");
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  const initials = userPrefix.slice(0, 2).toUpperCase();
  doc.text(initials, mg + 5 + (avatarSize - doc.getTextWidth(initials)) / 2, curY + 5 + 6.5);

  doc.setFontSize(11);
  doc.setTextColor(...BLACK);
  doc.text(`Patient ${userPrefix}`, mg + 5 + avatarSize + 4, curY + 9);
  doc.setFontSize(9);
  doc.setTextColor(...GRAY_TEXT);
  doc.text(patientInfo.patientId || "—", mg + 5 + avatarSize + 4, curY + 13);


  let badgeX = pageW - mg - 10;

  const stColor = reportStatus.toLowerCase() === "active" ? GREEN : BLUE;
  drawBadge(doc, badgeX, curY + 6, reportStatus.toUpperCase(), lighten(stColor), stColor, "right");

  const metricsY = curY + 22;
  const colW = (pageW - mg * 2 - 20) / 3;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.1);
  doc.line(mg + 5, metricsY - 4, pageW - mg - 5, metricsY - 4);

  drawMetricBox(doc, mg + 10, metricsY + 5, colW, "Avg Voids", averages.voids ?? "0.0");
  drawMetricBox(doc, mg + 10 + colW, metricsY + 5, colW, "Avg Leaks", averages.leaks ?? "0.0");
  drawMetricBox(doc, mg + 10 + colW * 2, metricsY + 5, colW, "Avg Intake", averages.fluid ?? "0", "oz");



  const cardFooterY = curY + cardH - 8;
  doc.line(mg + 5, cardFooterY, pageW - mg - 5, cardFooterY);

  doc.setFontSize(8);
  doc.setTextColor(...GRAY_TEXT);
  doc.text(`Days (Exc/Inc): ${excludedCount} / ${includedCount}`, mg + 10, cardFooterY + 5);


  if (tableRows && tableRows.length > 0) {
    curY += cardH + 12;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...BLACK);
    doc.text("DAILY DETAILS", mg, curY);
    curY += 5;

    let x = mg;
    COLS.forEach((col) => {
      const lines = col.label.split("\n");
      drawCell(doc, x, curY, col.w, HEAD_H, lines, {
        bg: GRAY_HEADER,
        bold: true,
        fontSize: FS_HEAD,
        align: col.align || "left",
        color: HEADER_TEXT
      });
      x += col.w;
    });
    curY += HEAD_H;

    tableRows.forEach((row, ri) => {
      const rec = row.record;
      const isExcDrop = rec?.dayInclude === "no";
      const isExcCheck = excludedDates.has(row.date);

      const statusLabel = isExcDrop ? "Excluded" : isExcCheck ? "Deselected" : "Included";
      const statusColor = isExcDrop ? RED : isExcCheck ? AMBER : GREEN;

      const reason = rec?.dayexcludeReason ? `${rec.dayexcludeReason} (By: ${rec.dayIncludeUserName || 'Admin'})` : "–";
      const lastMod = rec?.lastModified ? fmtDateTime(rec.lastModified) : "–";

      doc.setFont("helvetica", "normal");
      doc.setFontSize(FS - 2);

      const reasonLines = wrapText(doc, reason, COLS[8].w - 4);
      const lastModLines = wrapText(doc, lastMod, COLS[9].w - 4);

      ensureSpace(DATA_H);
      const rowBg = ri % 2 === 1 ? [245, 245, 245] : null;

      const vals = [
        [fmtDateShort(row.date)],
        [String(rec?.voidActivityCount ?? "0")],
        [String(rec?.totalLeaks ?? "0")],
        [String(rec?.intakeMl ?? "0")],
        [String(rec?.strongUrge ?? "0")],
        [String(rec?.intakeActivityCount ?? "0")],
        [String(rec?.totalActivityCount ?? "0")],
        [statusLabel],
        reasonLines,
        lastModLines,
      ];

      let lx = mg;
      COLS.forEach((col, ci) => {
        const isStatus = ci === 7;
        const isBold = ci === 0 || ci === 8;
        drawCell(doc, lx, curY, col.w, DATA_H, vals[ci], {
          fontSize: FS - 2,
          color: isStatus ? statusColor : BLACK,
          bold: isBold || isStatus,
          align: col.align || "left",
          bg: rowBg
        });
        lx += col.w;
      });
      curY += DATA_H;
    });
  }

  const totalPg = doc.getNumberOfPages();
  for (let p = 1; p <= totalPg; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...GRAY_TEXT);
    doc.text(
      `FemPulse Research Platform  `,
      mg,
      pageH - 5
    );
    doc.text(`Page ${p} of ${totalPg}`, pageW - mg, pageH - 5, {
      align: "right",
    });
  }

  const today = new Date().toISOString().split("T")[0];
  const year = new Date().getFullYear();
  const safeId = (patientInfo.patientId || "patient").replace(/[\s_]+/g, "-").replace(
    /[^a-zA-Z0-9@.-]/g,
    "-"
  );
  const defaultName = `${today}-FP-Detailed-Summary-${year}-${safeId}`;
  
  let finalName = (fileName || defaultName).replace(/\.pdf$/i, "");
  // Replace spaces and underscores with hyphens
  finalName = finalName.replace(/[\s_]+/g, "-");
  
  // Ensure the year is present in the final name
  if (!finalName.includes(String(year))) {
    finalName = `${finalName}-${year}`;
  }

  if (!shouldSave) {
    // Return the doc so caller can trigger download themselves
    return { doc, finalName };
  }

  doc.save(`${finalName}.pdf`);
}
